import React, { useRef, memo } from 'react';
import { NOT_FOUND } from 'redux-first-router';
import { connect } from 'react-redux';
import UniversalComponent from './UniversalComponent';
import ErrorBoundary from '../hocs/ErrorBoundary/ErrorBoundary';
import { addClass, removeClass } from '../utils/dom';
import { useLanguageURLRoots } from '../config';
import s from './Switcher.scss';

// Timers
let startTimer = null;
let middleTimer = null;
let endTimer = null;
let totalTimer = null;

const getCurrentPath = (setup, pathname, type) => {
	const path = decodeURIComponent(pathname);
	let currentPath = path.indexOf('#') !== -1 ? path.slice(0, path.indexOf('#')) : path;
	currentPath = Object.keys(setup.languages).length > 1 && !useLanguageURLRoots ? currentPath.replace(/^\/[a-z]{2}\/?/, '') : currentPath.replace(/^\//, '');
	if (currentPath === '') currentPath = 'start';
	if (type === NOT_FOUND) currentPath = '404';

	return currentPath;
};

const areEqual = (prevProps, { pageStatus, location, pageData, properties, setup, productCache = {} }) => {
	if (pageStatus.loading && !prevProps.pageStatus.loading) {
		const startTransition = 50;
		const middleTransition = 450;
		const endTransition = 500;
		const totalTransition = 1300;
		let transitionType = 'vertical';
		let isLoadingTransitioning = location.prev?.pathname && location.prev?.type && (pageStatus.loading || Date.now() - pageStatus.initiated < totalTransition);
		if (isLoadingTransitioning) {
			// Page is loaded and therefore (hopefully) even the productCache.
			const cache = productCache?.[setup.market]?.[setup.pricelist];
			const { categories = {} } = cache ? cache.filterArrays : {};
			// Transitioning from category to category.
			if (location.payload.productPage || location.type === 'START') {
				transitionType = 'horizontal';
			} else if (location.payload.productSwatch) {
				isLoadingTransitioning = false;
			} else if (Object.keys(categories).indexOf(location.prev.pathname.slice(1)) !== -1 && Object.keys(categories).indexOf(location.pathname.slice(1)) !== -1) {
				isLoadingTransitioning = false;
			} else if (typeof window !== 'undefined') {
				window.scrollTo(0, 0);
			}
		}

		// First render while loading set up timeouts.
		if (isLoadingTransitioning) {
			clearTimeout(startTimer);
			startTimer = setTimeout(() => {
				addClass(document.body, 'is_pageLoading__start');
			}, startTransition);

			clearTimeout(middleTimer);
			middleTimer = setTimeout(() => {
				addClass(document.body, `is_pageLoading__${transitionType}`);
			}, middleTransition);

			clearTimeout(endTimer);
			endTimer = setTimeout(() => {
				addClass(document.body, 'is_pageLoading__end');
			}, endTransition);
		}

		clearTimeout(totalTimer);
		totalTimer = setTimeout(() => {
			removeClass(document.body, ['is_pageLoading__end', 'is_pageLoading__horizontal', 'is_pageLoading__vertical', 'is_pageLoading__start']);
		}, totalTransition);
	}

	if (pageStatus.loading || properties.siteInitiated === false) return true;

	const currentPath = getCurrentPath(setup, location.pathname, location.type);
	const data = pageData[currentPath];
	const staticPages = ['receipt', 'search', 'selection', 'login', 'logout', 'register', 'forgot', 'account'];

	// Check if all data is available.
	if ((!pageData || !data) && staticPages.indexOf(pageStatus.pageType) === -1) return true;
	// Here we want to intercept "CatchAll" and read path API data to know which page to load.
	if (pageStatus.pageType === 'category' && Object.keys(productCache).length === 0) return true;

	return false;
};

const Page = ({ setup, pathname, type, pageStatus, pageData, missingPage }) => {
	const currentPath = getCurrentPath(setup, pathname, type);
	const data = type === NOT_FOUND ? { content: missingPage } : pageData[currentPath];
	let page = data?.pageType || pageStatus?.pageType;
	if (type === 'START') page = 'start';

	const parentContainer = useRef(null);
	const components = {
		start: 'Page/Page',
		page: 'Page/Page',
		404: 'Page/Page',
		search: 'Page/Page',
		product: 'Product/Product',
		category: 'Category/Category',
		selection: 'Selection/Selection',
		// login: 'Account/Login',
		// logout: 'Account/Logout',
		// register: 'Account/Register',
		// forgot: 'Account/Forgot',
		// account: 'Account/Account',
	};

	return (
		<div className={s.pageContainer_inner} ref={parentContainer}>
			{ components[page] && <UniversalComponent page={components[page]} pageType={page} data={data} currentPath={currentPath} /> }
		</div>
	);
};

const Switcher = ({ pageStatus, location, ...props }) => {
	if (pageStatus.loading) return true;
	return (
		<ErrorBoundary message="Error in Switcher">
			<div className={s.pageContainer}>
				<Page pathname={location.pathname} type={location.type} pageStatus={pageStatus} {...props} />
			</div>
		</ErrorBoundary>
	);
};

const mapState = ({ pageStatus, location, pageData, environment, productCache }) => ({
	pageStatus,
	location,
	pageData,
	properties: environment.properties,
	setup: environment.setup,
	missingPage: environment.missingPage,
	productCache,
});

export default connect(mapState)(memo(Switcher, areEqual));
